import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useSession } from "../session/SessionContext";
import styles from "./HelpPage.module.css";
import engineerContentEn from "../content/help/en/engineer.html?raw";
import managerContentEn from "../content/help/en/manager.html?raw";
import administratorContentEn from "../content/help/en/administrator.html?raw";
import engineerContentRo from "../content/help/ro/engineer.html?raw";
import managerContentRo from "../content/help/ro/manager.html?raw";
import administratorContentRo from "../content/help/ro/administrator.html?raw";

type Persona = "engineer" | "manager" | "administrator";

// The host serves the OpenAPI document at this path; Vite proxies it in development.
const openApiDocumentPath = "/openapi/v1.json";

const contentByLanguage: Record<string, Record<Persona, string>> = {
  en: {
    engineer: engineerContentEn,
    manager: managerContentEn,
    administrator: administratorContentEn,
  },
  ro: {
    engineer: engineerContentRo,
    manager: managerContentRo,
    administrator: administratorContentRo,
  },
};

// Permissions that put someone in the "manager" or "administrator" audience for the help content,
// mirrored from where each permission already gates a screen (`PortalNav`, `AdminGate` usages).
const ManagerPermissions = ["consumption.read.org", "consumption.read.others"];
const AdministratorPermissions = [
  "roles.administer",
  "entitlement.administer",
  "providers.administer",
  "scope.global",
  "accounts.administer",
  "request.approve",
  "settings.write",
  "audit.read",
  "identity.impersonate",
];

/**
 * The "Help" screen, opened from the header's help icon in a new browser tab/window so a person
 * can keep referring to it alongside whatever screen they came from. Content is organised by
 * persona — most people are Engineers looking up their own consumption, some are Managers who can
 * also see others' usage, and Administrators configure the portal itself — and is authored as
 * static per-locale HTML under `content/help/<lng>/`, not inline here or in the translation
 * catalogue, so it can grow long without bloating either.
 */
export function HelpPage(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const { snapshot } = useSession();

  const isSignedIn = snapshot.status === "signed-in" || snapshot.status === "unclassified";
  const held = snapshot.status === "signed-in" ? snapshot.permissions : [];
  const isManager = ManagerPermissions.some((permission) => held.includes(permission));
  const isAdministrator = AdministratorPermissions.some((permission) => held.includes(permission));

  const defaultPersona: Persona = isAdministrator ? "administrator" : isManager ? "manager" : "engineer";
  const [persona, setPersona] = useState<Persona>(defaultPersona);

  const personas: { id: Persona; label: string }[] = useMemo(
    () => [
      { id: "engineer", label: t("help.personas.engineer") },
      { id: "manager", label: t("help.personas.manager") },
      { id: "administrator", label: t("help.personas.administrator") },
    ],
    [t],
  );

  const language = i18n.resolvedLanguage ?? i18n.language;
  const contentByPersona = contentByLanguage[language] ?? contentByLanguage.en;

  return (
    <div>
      <h1>{t("help.title")}</h1>
      <p>{t("help.intro")}</p>

      <div className={styles.tabList} role="tablist" aria-label={t("help.personaTabsLabel")}>
        {personas.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={persona === id}
            className={persona === id ? styles.tabActive : styles.tab}
            onClick={() => setPersona(id)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className={styles.content} role="tabpanel" dangerouslySetInnerHTML={{ __html: contentByPersona[persona] }} />

      {isSignedIn && (
        <section aria-labelledby="help-api-heading">
          <h2 id="help-api-heading">{t("help.api.heading")}</h2>
          <p>{t("help.api.description")}</p>
          <p>
            <Link to="/rest-api">{t("help.api.reference")}</Link>
          </p>
          <p>
            <a href={openApiDocumentPath}>{t("help.api.json")}</a>
          </p>
        </section>
      )}
    </div>
  );
}
